import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { trackInitiateCheckout } from '../services/fbPixel';
import '../styles/PaymentPage.css';

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const user = auth.currentUser;
  
  // Get pricing tier from navigation state
  const { duration } = location.state || { duration: '24h' };
  
  // Map duration to plan details
  const planDetails = {
    '6h': { name: '6-Hour Pass', amount: 149, plan: '6h', durationDays: 0.25 },
    '12h': { name: '12-Hour Pass', amount: 229, plan: '12h', durationDays: 0.5 },
    '24h': { name: '24-Hour Pass', amount: 299, plan: '24h', durationDays: 1 },
    'monthly': { name: 'Monthly Pass', amount: 999, plan: 'monthly', durationDays: 30 }
  };
  
  const currentPlan = planDetails[duration] || planDetails['24h'];
  
  const [selectedMethod, setSelectedMethod] = useState('');
  const loading = false;
  const [error, setError] = useState(null);

  // Get PayPal URL based on plan
  const getPayPalUrl = () => {
    const paypalUrls = {
      '6h': 'https://www.paypal.com/ncp/payment/ATGSPCW2QBKVA',
      '12h': 'https://www.paypal.com/ncp/payment/NMXCLJ9WHCZ26',
      '24h': 'https://www.paypal.com/ncp/payment/AZGFDNM3LZCCG',
      'monthly': 'https://www.paypal.com/ncp/payment/YG8SV9LG4HTBW'
    };
    return paypalUrls[duration] || paypalUrls['24h'];
  };

  const paymentMethods = [
    {
      id: 'gcash',
      name: 'GCash',
      icon: '/images/payment/gcash_logo.png',
      description: 'Pay with GCash',
      enabled: true,
      type: 'internal' // Uses our QR code flow
    },
    {
      id: 'paypal',
      name: 'Debit or Credit Card',
      icon: '/images/payment/paypal_logo.png',
      description: 'Pay with Debit/Credit Card via PayPal',
      enabled: true,
      type: 'external', // Redirects to PayPal
      url: getPayPalUrl()
    },
    {
      id: 'maya',
      name: 'Maya',
      icon: '/images/payment/maya_logo.png',
      description: 'Coming Soon',
      enabled: false,
      type: 'internal'
    }
  ];

  const handlePayment = async () => {
    if (!selectedMethod) {
      setError('Please select a payment method');
      return;
    }

    const method = paymentMethods.find(m => m.id === selectedMethod);

    trackInitiateCheckout({ plan: currentPlan.name, amount: currentPlan.amount });
    
    if (method.type === 'external') {
      // Store payment intent in localStorage for return validation
      localStorage.setItem('paymentIntent', JSON.stringify({
        userId: user.uid,
        userEmail: user.email,
        plan: currentPlan.plan,
        amount: currentPlan.amount,
        method: selectedMethod,
        timestamp: Date.now()
      }));
      
      // For PayPal, add user ID to the URL via custom parameter
      // This lets the IPN handler identify which user made the payment
      const paypalUrl = `${method.url}?custom=${user.uid}&return=${encodeURIComponent(window.location.origin + '/payment/paypal/return')}`;
      
      // Redirect to PayPal
      window.location.href = paypalUrl;
    } else {
      // Navigate to payment instructions page for internal methods (GCash)
      navigate('/payment/instructions', {
        state: {
          method: selectedMethod,
          amount: currentPlan.amount,
          plan: currentPlan.plan,
          planName: currentPlan.name,
          durationDays: currentPlan.durationDays
        }
      });
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="payment-page">
      <div className="payment-container">
        <h1>Choose Payment Method</h1>
        <div className="plan-details">
          <h2>{currentPlan.name}</h2>
          <p className="price">₱{currentPlan.amount}</p>
          <p className="description">{currentPlan.durationDays >= 30 ? '30-day' : currentPlan.durationDays >= 1 ? '24-hour' : currentPlan.durationDays >= 0.5 ? '12-hour' : '6-hour'} access to all features</p>
        </div>

        <div className="payment-methods">
          {paymentMethods.map((method) => (
            <div
              key={method.id}
              className={`payment-method ${selectedMethod === method.id ? 'selected' : ''} ${!method.enabled ? 'disabled' : ''}`}
              onClick={() => method.enabled && setSelectedMethod(method.id)}
            >
              <div className="payment-icon-wrapper">
                <img src={method.icon} alt={method.name} />
              </div>
              <div className="method-details">
                <h3>{method.name}</h3>
                <p>{method.description}</p>
              </div>
              {!method.enabled && <div className="coming-soon-badge">Coming Soon</div>}
            </div>
          ))}
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          className="payment-button"
          onClick={handlePayment}
          disabled={loading || !selectedMethod}
        >
          {loading ? 'Processing...' : 'Pay Now'}
        </button>
      </div>
    </div>
  );
};

export default PaymentPage; 